import React, { useCallback, useState } from 'react';
import {
  RestAddressAttributesData,
  RestAddressData,
  ShippingAddressMutationError
} from '../../hooks/useAddress';
import { CheckoutSection } from '../CheckoutSection';
import { useCheckoutStepTracking, CheckoutStep } from '../../hooks/useCheckout';
import { ShippingAddressList } from './ShippingAddressList';
import { ShippingAddressForm } from './ShippingAddressForm';
import { ShippingAddressPreview } from './ShippingAddressPreview';

enum Status {
  Form,
  List,
  Preview
}

interface Props {
  addresses: RestAddressData[];
  defaultAddressId?: string;
  mutationFieldErrors: ShippingAddressMutationError;
  onSubmit: (
    address: RestAddressAttributesData,
    id: string | undefined
  ) => Promise<RestAddressData | undefined>;
  onDefaultAddressIdChange?: (id: string) => void;
}

const getStatus = (numberOfAddresses: number, defaultAddressId: string | undefined) => {
  if (defaultAddressId) {
    return Status.Preview;
  }
  if (numberOfAddresses) {
    return Status.List;
  }
  return Status.Form;
};

export const CheckoutShippingAddressContent: React.FC<Props> = ({
  addresses,
  defaultAddressId,
  mutationFieldErrors,
  onSubmit,
  onDefaultAddressIdChange
}) => {
  const [status, setStatus] = useState<Status>(getStatus(addresses.length, defaultAddressId));
  const [editingAddressId, setEditingAddressId] = useState<string | undefined>(undefined);
  const { trackCheckoutStepView, trackCheckoutStepComplete } = useCheckoutStepTracking(
    CheckoutStep.ShippingAddress
  );

  const handleScrollTo = useCallback(() => {
    const element = document.getElementById('shipping_address');

    if (element) {
      const { offsetTop } = element;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  }, []);

  const handleEdit = useCallback(
    (id: string) => {
      setEditingAddressId(addresses.find((address: RestAddressData) => address.id === id)?.id);
      setStatus(Status.Form);
      handleScrollTo();
    },
    [addresses, handleScrollTo]
  );

  const handleDefaultAddressIdChange = useCallback(
    (id: string) => {
      if (onDefaultAddressIdChange) {
        onDefaultAddressIdChange(id);
        setStatus(Status.Preview);
      }
      handleScrollTo();
      trackCheckoutStepComplete();
    },
    [onDefaultAddressIdChange, handleScrollTo, trackCheckoutStepComplete]
  );

  const handleAdd = useCallback(() => {
    setEditingAddressId(undefined);
    setStatus(Status.Form);
    handleScrollTo();
  }, [handleScrollTo]);

  const handleFormCancel = useCallback(() => {
    setEditingAddressId(undefined);
    setStatus(Status.List);
    handleScrollTo();
  }, [handleScrollTo]);

  const handleSubmit = useCallback(
    async (address: RestAddressAttributesData, id: string | undefined) => {
      try {
        const result = await onSubmit(address, id);

        if (result?.id && defaultAddressId !== result.id) {
          onDefaultAddressIdChange && (await onDefaultAddressIdChange(result.id));
        }

        setStatus(Status.Preview);
      } catch (error) {
        // do nothing
      }
    },
    [defaultAddressId, onDefaultAddressIdChange, onSubmit]
  );

  const handleAddressChange = useCallback(() => {
    setStatus(Status.List);

    trackCheckoutStepView();
  }, [trackCheckoutStepView]);

  return (
    <CheckoutSection id="shipping_address" title="Shipping Address">
      {status === Status.Preview && defaultAddressId && (
        <ShippingAddressPreview
          attributes={
            addresses.find((address: RestAddressData) => address.id === defaultAddressId)
              ?.attributes
          }
          onSwitch={handleAddressChange}
        />
      )}

      {status === Status.List && (
        <ShippingAddressList
          addresses={addresses}
          defaultAddressId={defaultAddressId}
          onAdd={handleAdd}
          onCancel={() => {
            setStatus(Status.Preview);
            handleScrollTo();
          }}
          onChange={handleDefaultAddressIdChange}
          onEdit={handleEdit}
        />
      )}

      {status === Status.Form && (
        <ShippingAddressForm
          address={addresses.find(address => address.id === editingAddressId)}
          mutationFieldErrors={mutationFieldErrors}
          onSubmit={handleSubmit}
          onCancel={addresses.length ? handleFormCancel : undefined}
        />
      )}
    </CheckoutSection>
  );
};
